import { useTranslation } from "react-i18next";
import { AlertTriangle, Pause, Play, RotateCcw, Square } from "lucide-react";

import type { BlsChannel } from "../../../application/useBlsSession";
import { usePassCount } from "../../../application/useBlsControls";
import type { BlsSettings, BlsState } from "../../../domain/blsChannel";
import BlsStimulus from "../BlsStimulus";

type Props = {
    channel: BlsChannel;
    settings: BlsSettings;
    /** The number the set on screen will be saved under; null until known. */
    setNumber: number | null;
    onStart: () => void;
};

type Phase = "ready" | "running" | "paused" | "halted";

function phaseOf(channel: BlsChannel): Phase {
    if (!channel.state) return "ready";
    if (channel.state.running) return "running";
    return channel.haltedByPatient ? "halted" : "paused";
}

const PHASE_TONE: Record<Phase, string> = {
    ready: "bg-room-active text-room-ink-muted",
    running: "bg-room-ok-tint text-room-ok",
    paused: "bg-room-warn-tint text-room-warn",
    halted: "bg-room-live-tint text-room-live",
};

/**
 * The set itself: what state it is in, how many passes, the one preview of
 * what the patient sees, and the buttons that drive it — large, because they
 * are pressed while watching the patient rather than the screen.
 */
export default function BoardStage({ channel, settings, setNumber, onStart }: Props) {
    const { t } = useTranslation();
    const { state } = channel;
    const passes = usePassCount(channel);
    const phase = phaseOf(channel);
    const preview: BlsState = state ?? { settings, running: false, cycles: 0, atEpochMs: 0 };

    return (
        <section
            aria-labelledby="emdr-stage-title"
            className="flex flex-col gap-4 rounded-3xl border border-room-line-soft bg-room-raised p-4 sm:p-6"
            data-testid="emdr-stage"
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 id="emdr-stage-title" className="text-base font-semibold text-room-ink">
                    {setNumber === null ? t("emdr.console.stage.setUnnumbered") : t("emdr.console.stage.set", { number: setNumber })}
                </h2>
                <p
                    role="status"
                    aria-live="polite"
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${PHASE_TONE[phase]}`}
                    data-testid="emdr-phase"
                >
                    <span aria-hidden="true" className={`size-1.5 rounded-full bg-current ${phase === "running" ? "animate-pulse motion-reduce:animate-none" : ""}`} />
                    {t(`emdr.console.phase.${phase}`)}
                </p>
            </div>

            <div className="flex items-end gap-6">
                <div className="flex flex-col">
                    <span className="text-6xl font-semibold leading-none tabular-nums text-room-ink" data-testid="bls-passes">
                        {passes}
                    </span>
                    <span className="mt-1 text-xs text-room-ink-muted">{t("emdr.passes")}</span>
                </div>
                <div className="min-w-0 flex-1">
                    <span className="mb-1 block text-xs text-room-ink-muted">{t("emdr.preview")}</span>
                    <BlsStimulus state={preview} cyclesNow={channel.cyclesNow} className="h-24 w-full rounded-xl" />
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                {phase === "ready" && (
                    <button type="button" onClick={onStart} className="room-control room-control-primary min-h-12 px-6 text-base">
                        <Play size={18} aria-hidden="true" />
                        <span>{t("emdr.start")}</span>
                    </button>
                )}
                {phase === "running" && (
                    <button type="button" onClick={channel.pause} className="room-control room-control-primary min-h-12 px-6 text-base">
                        <Pause size={18} aria-hidden="true" />
                        <span>{t("emdr.pause")}</span>
                    </button>
                )}
                {(phase === "paused" || phase === "halted") && (
                    <>
                        <button type="button" onClick={onStart} className="room-control room-control-primary min-h-12 px-6 text-base">
                            <RotateCcw size={18} aria-hidden="true" />
                            <span>{t("emdr.newSet")}</span>
                        </button>
                        <button type="button" onClick={channel.resume} className="room-control min-h-12 px-4">
                            <Play size={16} aria-hidden="true" />
                            <span>{t("emdr.console.stage.continueSet")}</span>
                        </button>
                    </>
                )}
                {state && (
                    <button type="button" onClick={channel.stop} className="room-control min-h-12 px-4">
                        <Square size={16} aria-hidden="true" />
                        <span>{t("emdr.console.stage.stop")}</span>
                    </button>
                )}
                <p className="ml-auto hidden text-xs text-room-ink-muted sm:block">
                    <kbd className="rounded border border-room-line px-1.5 py-0.5 font-sans text-xs">{t("emdr.console.stage.spaceKey")}</kbd>{" "}
                    {t("emdr.console.stage.spaceHint")}
                </p>
            </div>

            {phase === "halted" && (
                <p role="alert" className="rounded-xl bg-room-live-tint px-3 py-2 text-sm text-room-live">
                    {t("emdr.patientStopped")}
                </p>
            )}
            {channel.transportFailed && (
                <p role="status" className="text-xs text-room-warn">
                    {t("emdr.transportFailed")}
                </p>
            )}
            {phase === "ready" && (
                <p className="flex gap-2 text-xs text-room-ink-muted">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-room-warn" aria-hidden="true" />
                    {t("emdr.photosensitivity")}
                </p>
            )}
        </section>
    );
}
